import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ENDPOINTS,
    isNgRxEnabled,
    NOTIFICATION_STATUS_CODE,
    NotificationDurationModel,
    notificationIdToState,
    ODataDurationResponseModel,
    OnlineOnlyService,
    travelStatusRecorded,
} from '@evorait/pace-core';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'pace-duration',
    templateUrl: './duration.component.html',
    styleUrls: ['./duration.component.scss'],
    standalone: false,
})
export class DurationComponent implements OnInit, OnDestroy {
    @Input()
    public model: NotificationDurationModel;
    public durationForm: FormGroup;
    public formChildren: FormArray;
    public durationChangeInProgress = false;

    private destroy$ = new Subject<void>();
    private readonly store = inject(Store);
    private readonly ngrxEnabled = isNgRxEnabled();

    public constructor(
        private readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
    ) {}

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.durationForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
    }

    public closeModal(): void {
        this.modalInstance.hide();
    }

    public changeDuration(): void {
        this.durationChangeInProgress = true;
        this.onlineOnlyService
            .post<ODataDurationResponseModel>(ENDPOINTS.NotificationDurationChange, this.model.getOutModel())
            .pipe(
                takeUntil(this.destroy$),
                catchError((error) => {
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('NOTIFICATION_DETAILS_CHANGE_DURATION_ERROR_TITLE'),
                        `${<string>this.translate.instant('NOTIFICATION_DETAILS_CHANGE_DURATION_ERROR_BODY')} ${JSON.stringify(error)}`,
                    );

                    return EMPTY;
                }),
                finalize(() => (this.durationChangeInProgress = false)),
            )
            .subscribe(() => {
                this.recordDuration();
                this.closeModal();
            });
    }

    /**
     * Rewrites the notification's DURA with the accepted end, the way a travel-status submit records
     * its statuses, so the new window shows before the next load brings the backend's DURA task.
     * Only the notification slice serves the screen with NgRx enabled; without it there is nothing
     * to update.
     */
    private recordDuration(): void {
        if (!this.ngrxEnabled) {
            return;
        }
        this.store.dispatch(
            travelStatusRecorded({
                id: notificationIdToState(this.model.notificationIdentifier),
                submission: {
                    statusCode: NOTIFICATION_STATUS_CODE.DURA,
                    start: this.model.startDateTime.value.toISOString(),
                    end: this.model.endDateTime.value.toISOString(),
                },
            }),
        );
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
